/**
 * Motion, coverage and pause control for the Timecard announcement marquee.
 *
 * Scrolling is opt-in. The CSS animates only while this element carries `data-ready="true"`, which
 * is set here — so the rendered default, and what a visitor without JavaScript keeps, is a static
 * wrapped readable bar with one group of messages and no controls. A CSS-only marquee would instead
 * scroll forever for precisely the visitor who could not stop it.
 *
 * Coverage is measured rather than assumed. The section renders exactly one group; the copies needed
 * to span the viewport are cloned from its real width. A fixed server-side repeat count cannot be
 * right at more than one size: it leaves empty stretches on a wide screen and pointless overdraw on
 * a narrow one, and a minimum width big enough to hide that introduces exactly the large blank
 * regions it was meant to prevent.
 *
 * There is no separate visible button. While motion is running the strip itself is the control:
 * it takes `role="button"`, becomes focusable, and reports its state through `aria-pressed`. Both
 * are removed again in the static state, where there would be nothing to toggle — a focusable
 * element that does nothing is a worse outcome than no control at all.
 *
 * No animation loop runs in JavaScript. The CSS owns the motion; this file only measures, clones,
 * and flips state.
 */

/** Bounds the clone count so a pathologically narrow group cannot fill the DOM. */
const MAX_COPIES = 24;

class TimecardMarquee extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-marquee-track]');
    this.group = this.querySelector('[data-marquee-group]');
    this.viewport = this.querySelector('[data-marquee-viewport]');
    this.instruction = this.querySelector('[data-marquee-instruction]');
    if (!this.track || !this.group) return;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.onMotionChange = this.onMotionChange.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onResize = this.onResize.bind(this);

    this.reducedMotion.addEventListener('change', this.onMotionChange);
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);

    if ('ResizeObserver' in window) {
      // Watching the host rather than the window also catches a container that changes width
      // without the viewport doing so, which is what the theme editor does while resizing panels.
      this.observer = new ResizeObserver(this.onResize);
      this.observer.observe(this);
    } else {
      window.addEventListener('resize', this.onResize);
    }

    this.onMotionChange();

    // Web fonts land after first layout and change the group's measured width. Re-measuring once
    // they are ready avoids a strip that was sized against fallback metrics and no longer spans.
    document.fonts?.ready.then(() => {
      if (this.isConnected) this.measureAndFill();
    });
  }

  disconnectedCallback() {
    this.reducedMotion?.removeEventListener('change', this.onMotionChange);
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
    this.observer?.disconnect();
    window.removeEventListener('resize', this.onResize);
    cancelAnimationFrame(this.frame);

    // Returned to the state the server rendered. Without this, a re-connect — which the theme
    // editor does on every edit — would measure a track that already holds clones and append a
    // second full set on top of them.
    this.reset();
  }

  /** Strips clones, motion and control affordances, leaving the single accessible group. */
  reset() {
    for (const clone of this.track.querySelectorAll('[data-marquee-clone]')) clone.remove();
    this.removeAttribute('data-ready');
    this.removeAttribute('data-measuring');
    this.style.removeProperty('--tc-marquee-copies');
    this.removeAttribute('role');
    this.removeAttribute('tabindex');
    this.removeAttribute('aria-pressed');
    this.removeAttribute('aria-label');
    this.removeAttribute('aria-describedby');
    // The group goes back to being the page's readable copy of the messages.
    this.viewport?.removeAttribute('aria-hidden');
    if (this.instruction) this.instruction.textContent = '';
  }

  onMotionChange() {
    if (this.reducedMotion.matches) {
      this.reset();
      return;
    }
    this.measureAndFill();
  }

  onResize() {
    // Coalesced to one frame: a drag-resize fires continuously, and re-cloning per event would
    // rebuild the track dozens of times a second.
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.measureAndFill());
  }

  measureAndFill() {
    if (this.reducedMotion.matches) return;

    // Measured from the single source, with any previous clones removed first so the reading is
    // one group rather than the whole filled track.
    for (const clone of this.track.querySelectorAll('[data-marquee-clone]')) clone.remove();

    // Measured in the scrolling layout, not the static one. A wrapped group reports about the
    // viewport width, which makes the arithmetic conclude two copies are enough and leaves the
    // strip short once nowrap actually applies.
    this.setAttribute('data-measuring', '');
    const groupWidth = this.group.getBoundingClientRect().width;
    const viewport = this.getBoundingClientRect().width;
    if (groupWidth <= 0 || viewport <= 0) {
      this.removeAttribute('data-measuring');
      return;
    }

    // One set to cover the viewport, plus one more so there is always a full group queued to slide
    // in behind the shift. Two is the floor: a single group has nothing to loop against.
    const needed = Math.min(MAX_COPIES, Math.max(2, Math.ceil(viewport / groupWidth) + 1));

    const fragment = document.createDocumentFragment();
    for (let i = 1; i < needed; i += 1) {
      const clone = this.group.cloneNode(true);
      // Decorative repetition. Announcing the same sentence a dozen times is noise, not content.
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('data-marquee-clone', '');
      clone.removeAttribute('data-marquee-group');
      fragment.append(clone);
    }
    this.track.append(fragment);

    // The keyframe shifts by 100%/copies, which is exactly one group because the groups are
    // identical and the track carries no gap between them.
    this.style.setProperty('--tc-marquee-copies', String(needed));
    this.removeAttribute('data-measuring');
    this.dataset.ready = 'true';

    this.setAttribute('role', 'button');
    this.setAttribute('tabindex', '0');

    // The whole strip becomes the control, so its accessible name is the announcement text itself.
    // Naming it "Pause" would replace the message for a screen reader rather than describe it, and
    // the repeated copies would be a second and third reading of the same sentence — so the visible
    // text is hidden and the name carries it exactly once.
    if (this.dataset.messages) this.setAttribute('aria-label', this.dataset.messages);
    this.viewport?.setAttribute('aria-hidden', 'true');

    if (this.instruction && this.dataset.instructionId) {
      this.setAttribute('aria-describedby', this.dataset.instructionId);
    }

    this.setPaused(this.dataset.paused === 'true');
  }

  onClick() {
    if (this.dataset.ready !== 'true') return;
    this.setPaused(this.dataset.paused !== 'true');
  }

  onKeydown(event) {
    if (this.dataset.ready !== 'true') return;
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
    // Space would otherwise scroll the page out from under the control.
    event.preventDefault();
    this.setPaused(this.dataset.paused !== 'true');
  }

  setPaused(paused) {
    this.dataset.paused = paused ? 'true' : 'false';
    this.setAttribute('aria-pressed', paused ? 'true' : 'false');
    // What activating it will do travels in the description, leaving the name free to carry the
    // announcement itself.
    if (this.instruction) {
      this.instruction.textContent = paused ? this.dataset.labelPlay : this.dataset.labelPause;
    }
  }
}

if (!customElements.get('timecard-marquee')) {
  customElements.define('timecard-marquee', TimecardMarquee);
}
